import { splitPath } from "@peekie/case-spec";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cloneRoot(root: unknown): unknown {
  return structuredClone(root);
}

/** Read value at path. Missing path returns undefined. */
export function getAtPath(root: unknown, path: string): unknown {
  if (!path) return root;
  let cur: unknown = root;
  for (const seg of splitPath(path)) {
    if (typeof seg === "number") {
      if (!Array.isArray(cur)) return undefined;
      cur = cur[seg];
      continue;
    }
    if (!isRecord(cur)) return undefined;
    cur = cur[seg];
  }
  return cur;
}

/**
 * Immutable set at path. Creates intermediate objects/arrays as needed.
 * Returns a new root.
 */
export function setAtPath(
  root: unknown,
  path: string,
  value: unknown,
): unknown {
  if (!path) return value;
  const segments = splitPath(path);
  const next = cloneRoot(root ?? {});
  let cur: unknown = next;

  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]!;
    const nextSeg = segments[i + 1]!;
    if (typeof seg === "number") {
      if (!Array.isArray(cur)) {
        throw new Error(`expected array at segment ${i} of ${path}`);
      }
      if (cur[seg] === undefined || cur[seg] === null) {
        cur[seg] = typeof nextSeg === "number" ? [] : {};
      }
      cur = cur[seg];
      continue;
    }
    if (!isRecord(cur)) {
      throw new Error(`expected object at segment ${i} of ${path}`);
    }
    if (cur[seg] === undefined || cur[seg] === null) {
      cur[seg] = typeof nextSeg === "number" ? [] : {};
    }
    cur = cur[seg];
  }

  const last = segments[segments.length - 1]!;
  if (typeof last === "number") {
    if (!Array.isArray(cur)) {
      throw new Error(`expected array at end of ${path}`);
    }
    cur[last] = value;
  } else {
    if (!isRecord(cur)) {
      throw new Error(`expected object at end of ${path}`);
    }
    cur[last] = value;
  }
  return next;
}

/** Immutable delete of object key or array index at path. */
export function deleteAtPath(root: unknown, path: string): unknown {
  if (!path) return root;
  const segments = splitPath(path);
  if (segments.length === 0) return root;
  const parentPath = joinPath(segments.slice(0, -1));
  const last = segments[segments.length - 1]!;
  const parent = parentPath ? getAtPath(root, parentPath) : root;

  if (typeof last === "number") {
    if (!Array.isArray(parent)) return root;
    const nextArr = parent.filter((_, i) => i !== last);
    return parentPath ? setAtPath(root, parentPath, nextArr) : nextArr;
  }

  if (!isRecord(parent)) return root;
  const nextObj = { ...parent };
  delete nextObj[last];
  return parentPath ? setAtPath(root, parentPath, nextObj) : nextObj;
}

/** Append item to array at path. */
export function appendAtPath(
  root: unknown,
  arrayPath: string,
  item: unknown,
): unknown {
  const arr = getAtPath(root, arrayPath);
  const next = Array.isArray(arr) ? [...arr, item] : [item];
  return setAtPath(root, arrayPath, next);
}

/** Remove array item at index. */
export function removeAtIndex(
  root: unknown,
  arrayPath: string,
  index: number,
): unknown {
  return deleteAtPath(root, `${arrayPath}[${index}]`);
}

/** Move array item up (-1) or down (+1). */
export function moveAtIndex(
  root: unknown,
  arrayPath: string,
  index: number,
  delta: -1 | 1,
): unknown {
  const arr = getAtPath(root, arrayPath);
  if (!Array.isArray(arr)) return root;
  const target = index + delta;
  if (target < 0 || target >= arr.length) return root;
  const next = [...arr];
  const tmp = next[index]!;
  next[index] = next[target]!;
  next[target] = tmp;
  return setAtPath(root, arrayPath, next);
}

export function joinPath(segments: Array<string | number>): string {
  let out = "";
  for (const seg of segments) {
    if (typeof seg === "number") {
      out += `[${seg}]`;
    } else if (!out) {
      out = seg;
    } else {
      out += `.${seg}`;
    }
  }
  return out;
}

export function childPath(parent: string, key: string | number): string {
  if (typeof key === "number") {
    return parent ? `${parent}[${key}]` : `[${key}]`;
  }
  return parent ? `${parent}.${key}` : key;
}
